'use client';

import { useTranslations } from 'next-intl';
import React, { useContext, useState } from 'react';
import Image from 'next/image';
import styles from './RareProductSelectionSection.module.css';
import { useSearchParams } from 'next/navigation';
import { analytics } from 'lib/analytics/analytics';
import { RareCarousel } from './RareCarousel';
import { RareSelectionContext } from './RareSelectionContext';
import {
  RareColors,
  RareColorNameImages,
  RARE_COLOR_ORDER,
  RarePriceData,
} from 'content/products/rare/productMap/constants';
import { RareCarouselData } from 'content/rare';
import { useFormatPrice } from 'hooks/pricing/useFormattedPrice';
import { useWindowSize } from 'hooks/dom/useWindowSize';
import { CustomImage, LearnMoreButton, useModal } from 'components/shared';
import { useCartStore, useShallow } from 'components/shared/Cart';
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import {
  getLifetimePowerPlugs,
  getLifetimeSoftwareFeatures,
} from 'content/products/rare/lifetimeSoftwareFeatures';
// import ChevronRight from 'assets/images/chevronRight.svg';
import DeliveryIcon from 'assets/deliveryIcon.svg';
import { GlobalUIContext } from 'contexts/GlobalUIContext';
import {
  ShippingBox,
  InfoIcon,
  CheckIcon,
  Charger,
  Battery,
  Return,
  HandWithRing,
  CircleTick,
  ShoppingTrolley,
  BoxOutline,
  UhxInfoReplacement,
  UhxInfoShield,
  WeightLoss,
  ArrowTopRight,
  WarningTriangle,
} from 'assets/icons';
import { PanelContainer } from 'components/ring-air/ProductSelectionSection';
// import { TitleBar } from 'components/ring-air/ProductSelectionSection/TitleBar';
import { RegionSelector } from 'components/shared/RegionSelector';
import {
  PrimaryOffers,
  PrimaryOffersTransitionTile,
} from 'components/ring-air/ProductSelectionSection/PrimaryOffers';
import { OffersModalNewProvider } from 'components/shared/OffersModal';

import { isMarketingBannerActive } from 'components/shared/Header/MarketingBanner';
import { OffersCarousel } from 'components/ring-air/OffersCarousel';
import { RareInfoModal } from 'components/shared/InfoModals/rareInfoModals';
import { Currency } from 'utils/Currency';
import { formatPriceWithSymbol } from 'hooks/pricing/useFormattedPriceNew';
import { getRareAddonPricing } from 'content/products/rare/addonPricing';
import { Membership } from 'assets/icons/Membership';
import { Support } from 'assets/icons/Support';
import { RingSizes } from 'content/products/ring';
import CustomLink from 'components/shared/CustomLink';

// CheckIcon component from HsaFsaComponent
const HsaFsaCheckIcon = ({
  className,
  style,
  fill = 'black',
}: {
  className?: string;
  style?: React.CSSProperties;
  fill?: string;
}) => {
  return (
    <svg
      className={className}
      style={style}
      width="23"
      height="25"
      viewBox="0 0 23 25"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M0.0445803 12.2808C0.655407 6.11158 6.63525 1.48262 12.762 2.25157C13.9411 2.39926 15.072 2.80381 16.1773 3.21076L15.4581 4.53916C15.292 4.65635 15.0568 4.43562 14.893 4.37382C9.48871 2.35191 3.44627 5.35306 1.91157 10.8979C-0.140032 18.3072 6.69464 25.0906 14.0928 22.9988C20.1409 21.2891 22.9991 14.3092 19.9659 8.77482C19.8366 8.53803 19.1544 7.61657 19.1431 7.505C19.1143 7.21043 19.7564 6.40295 19.9105 6.10195C22.6123 9.05575 23.4205 13.3925 22.1596 17.1795C20.4972 22.1721 15.4413 25.4108 10.2023 24.8795C5.01949 24.3537 0.504506 19.98 0.0445803 14.7691C-0.0116061 14.1326 -0.0180274 12.9117 0.0445803 12.2808Z"
        fill={fill ?? 'black'}
      />
      <path
        d="M21.0742 0.723199L11.081 18.1402L6.15346 12.8362L6.15186 12.65L7.22823 11.6402L10.8402 15.5717L19.7096 0L21.0742 0.723199Z"
        fill={fill ?? 'black'}
      />
    </svg>
  );
};

const RPSS_PADDING_TOP = isMarketingBannerActive() ? '80px' : '48px';

const LeftPanel = ({ children }: { children?: React.ReactNode }) => (
  <div className={styles.leftPanel}>{children}</div>
);

const StickyPanel = ({
  $headerVisible,
  children,
}: {
  $headerVisible: boolean;
  children?: React.ReactNode;
}) => (
  <div
    className={styles.stickyPanel}
    data-header-visible={$headerVisible ? 'true' : 'false'}
  >
    {children}
  </div>
);

const RightPanel = ({ children }: { children?: React.ReactNode }) => (
  <div className={styles.rightPanel}>{children}</div>
);

const Section = ({
  show,
  className,
  children,
}: {
  show?: boolean;
  className?: string;
  children?: React.ReactNode;
}) => (
  <div
    className={`${styles.section}${className ? ` ${className}` : ''}`}
    data-show={show ? 'true' : 'false'}
  >
    {children}
  </div>
);

const RareProductSelectionSectionWrapper = ({
  bgColor,
  children,
}: {
  bgColor?: string;
  children?: React.ReactNode;
}) => (
  <section
    className={styles.wrapper}
    style={
      {
        ['--rpss-bg']: bgColor || '#EBE3DB',
        ['--rpss-pt']: RPSS_PADDING_TOP,
      } as React.CSSProperties
    }
  >
    {children}
  </section>
);

// Offers strip slot. Renders both the desktop (PrimaryOffers) and mobile
// (PrimaryOffersTransitionTile) variants at every breakpoint so the SSR markup
// is correct without a JS width gate, toggling visibility via CSS. The reserved
// min-height (mobile 58px / desktop 8rem) keeps the swap from shifting layout.
const PrimaryOffersSlot = ({ children }: { children?: React.ReactNode }) => (
  <div className={styles.primaryOffersSlot}>{children}</div>
);

const DesktopPrimaryOffers = ({ children }: { children?: React.ReactNode }) => (
  <div className={styles.desktopPrimaryOffers}>{children}</div>
);

const MobilePrimaryOffers = ({ children }: { children?: React.ReactNode }) => (
  <div className={styles.mobilePrimaryOffers}>{children}</div>
);

const useSizingInfoData = () => {
  const t = useTranslations('ring');
  return [
    {
      icon: <ShoppingTrolley />,
      listStep: t('ring.productSelection.sizingSection.modalNew.stepA.step'),
      listTitle: t('ring.productSelection.sizingSection.modalNew.stepA.title'),
      listDescription: t(
        'ring.productSelection.sizingSection.modalNew.stepA.subTitle',
      ),
    },
    {
      icon: <BoxOutline fill="#000000" />,
      listStep: t('ring.productSelection.sizingSection.modalNew.stepB.step'),
      listTitle: t('ring.productSelection.sizingSection.modalNew.stepB.title'),
      listDescription: t(
        'ring.productSelection.sizingSection.modalNew.stepB.subTitle',
      ),
    },
    {
      icon: <HandWithRing />,
      listStep: t('ring.productSelection.sizingSection.modalNew.stepC.step'),
      listTitle: t('ring.productSelection.sizingSection.modalNew.stepC.title'),
      listDescription: (
        <>
          {t('ring.productSelection.sizingSection.modalNew.stepC.subTitle')}{' '}
          <CustomLink
            target={'_blank'}
            href="/sizing"
            style={{ textDecoration: 'underline' }}
          >
            {/* here */}
            {t('ring.productSelection.sizingSection.modalNew.stepC.HERE')}{' '}
          </CustomLink>
          . {/* Take time to ensure the best fit. */}
          {t('ring.productSelection.sizingSection.modalNew.stepC.endText')}
        </>
      ),
    },
    {
      icon: <CircleTick />,
      listStep: t('ring.productSelection.sizingSection.modalNew.stepD.step'),
      listTitle: t('ring.productSelection.sizingSection.modalNew.stepD.title'),
      listDescription: t(
        'ring.productSelection.sizingSection.modalNew.stepD.subTitle',
      ),
    },
  ];
};

const uhxlifetimeInfoData = [
  {
    icon: <Membership />,
    listTitle: <span>Membership for life</span>,
    listDescription: <>Enjoy complimentary lifetime* access to UltrahumanX.</>,
  },
  {
    icon: <Support />,
    listTitle: <span>Priority support, always</span>,
    listDescription: (
      <>
        No request too big, no question too small. We’re here to make your Rare
        Ring work perfectly for you.
      </>
    ),
  },
  {
    icon: <WeightLoss />,
    listTitle: <span>Complimentary resizing</span>,
    listDescription: (
      <>
        Need a different fit? Rare users get a one-time complimentary size
        adjustment within one year of purchase.
      </>
    ),
  },
  {
    icon: <UhxInfoReplacement />,
    listTitle: <span>Exclusive upgrade program</span>,
    listDescription: (
      <>
        Seamlessly trade your Rare Ring for future collections and enhanced
        editions.*
      </>
    ),
  },
  {
    icon: <UhxInfoShield />,
    listTitle: <span>Lifetime replacement coverage</span>,
    listDescription: (
      <>
        Your Ring Rare is protected for life. We’ll repair or replace it for any
        manufacturing defects.*
      </>
    ),
  },
];

interface RareProductSelectionSectionProps {
  title?: string;
  bgColor?: string;
  offersData?: any[];
  offersBackgroundColor?: string;
  dealsBackgroundColor?: string;
  secondaryOfferData?: any[];
}

const RareProductSelectionSection: React.FC<
  RareProductSelectionSectionProps
> = ({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  title = 'Ultrahuman Ring Rare',
  bgColor = '#EBE3DB',
  offersData,
  offersBackgroundColor = '#EBE3DB',
  dealsBackgroundColor = '#F4F0EB',
  secondaryOfferData,
}) => {
  const t = useTranslations('ring');
  const sizingInfoData = useSizingInfoData();
  const { width } = useWindowSize();
  const rareContext = useContext(RareSelectionContext);
  const cartContext = useCartStore(
    useShallow((s) => ({ store: s.store, currency: s.currency })),
  );
  const globalUI = useContext(GlobalUIContext);
  const searchParams = useSearchParams();
  const [showSizeSelection, setShowSizeSelection] = useState(false);

  const {
    color,
    setColor,
    size,
    currencySymbol,
    effectivePrice,
    checkout,
    isLoading,
    notServicable,
    notServicableMessage,
    priceDrop,
    price,
    isDiscountEnabled,
    showStrikedPrice,
    isSizingKitSelected,
    handleSizeSelection,
    handleSizingKitClick,
    getUhxCoveragePrice,
    getLifetimeSoftwarePrice,
    affiliateDiscount,
    effectiveDiscount,
    affiliateDetails,
    shippingEstimate,
  } = rareContext;

  // Determine if tax note should be shown based on tax_incl from RarePriceData
  const shouldShowTaxNote = () => {
    if (!cartContext.store || !color) return true; // Default to showing if no data

    const priceData = RarePriceData[cartContext.store as ShopifyStore]?.[color];
    if (!priceData) return true; // Default to showing if no price data

    return !priceData.tax_incl; // Show note only when tax is NOT included
  };

  const ModalClient = useModal();
  const formatPriceWithComma = useFormatPrice();
  const powerplugsInfoData = getLifetimePowerPlugs(cartContext.store);

  const calculateLifetimeSoftwarePrice = () => {
    const store = cartContext.store || ShopifyStore.ROW; // Fallback to ROW if store is null
    const lifetimeSoftwarePrice = getRareAddonPricing(
      store,
      'lifetimeSoftware',
    );
    const currency = cartContext.currency ?? Currency.usd;
    const formattedPrice = formatPriceWithSymbol(
      lifetimeSoftwarePrice,
      currency,
    );
    return formattedPrice;
  };

  const uhxCoveragePrice = () => {
    const store = cartContext.store || ShopifyStore.ROW; // Fallback to ROW if store is null
    const uhxCoveragePrice = getRareAddonPricing(store, 'uhxCoverage');
    const currency = cartContext.currency ?? Currency.usd;
    const formattedPrice = formatPriceWithSymbol(uhxCoveragePrice, currency);
    return formattedPrice;
  };

  // Not servicable message component
  const NotServicableMessage = notServicable ? (
    notServicableMessage ? (
      <div className="not-servicable-message-container">
        <WarningTriangle
          fill={'rgba(0, 0, 0, 0.6)'}
          style={{ width: '20px', height: '20px' }}
        />
        {notServicableMessage}
      </div>
    ) : (
      <div className="not-servicable-message-container center">
        Currently Unavailable
      </div>
    )
  ) : null;

  const openUhxInfoModal = () => {
    ModalClient.set(
      <RareInfoModal
        title="UltrahumanX Membership"
        subtitle="Complimentary access for life"
        headerImage="web_v2/rare-info-modals/uhx-lifetime.png"
        list={true}
        data={uhxlifetimeInfoData}
        valuedAt={uhxCoveragePrice()}
        mainDescription="Exclusive benefits and lifetime coverage, crafted to protect your Ultrahuman Ring Rare."
        bottomContent={
          <>
            <p className="disclaimer">
              *Subject to Ultrahuman&apos;s Policy and{' '}
              <CustomLink
                className="redirect"
                href={{
                  pathname: `/termsAndCondition/`,
                  query: Object.fromEntries(searchParams?.entries() ?? []),
                }}
                onClick={() => {
                  ModalClient.hide();
                }}
              >
                Terms of Use.
              </CustomLink>
            </p>
          </>
        }
      />,
    );
    ModalClient.show();
  };

  const openPowerPlugsInfoModal = () => {
    ModalClient.set(
      <RareInfoModal
        title="All PowerPlugs"
        subtitle="Complimentary access for life"
        valuedAt={calculateLifetimeSoftwarePrice()}
        data={powerplugsInfoData}
      />,
    );
    ModalClient.show();
  };

  const openSizeInfoModal = () => {
    ModalClient.set(
      <RareInfoModal
        title="How Ring Sizing Works"
        subtitle="Ultrahuman Ring Rare uses custom sizes, distinct from standard smart ring and market sizes. A precise fit is essential for accurate data tracking and comfort."
        list={true}
        data={sizingInfoData}
        bottomContent={
          <>
            <CustomLink
              className="video"
              target="_blank"
              href="https://www.youtube.com/watch?v=t9ca-Lq70q4"
            >
              <CustomImage
                width={540}
                height={270}
                alt="Sizing Kit Video"
                src={
                  width < 900
                    ? 'web_v2/ring-buy/thumbnail_desktop.png'
                    : 'web_v2/ring-buy/thumbnail_mobile.png'
                }
              />
              Watch our video tutorial for quick guidance
            </CustomLink>
            <hr />
            <p className="disclaimer">
              {t('ring.productSelection.sizingSection.modalNew.disclaimer')}{' '}
              <a href="mailto:support@ultrahuman.com">support@ultrahuman.com</a>
              .
            </p>
          </>
        }
      />,
    );
    ModalClient.show();
  };

  const handleCheckout = () => {
    if (isLoading || notServicable || !color || !size) return;

    analytics.track(`CLICK: Product Selection, Add to Cart`);
    checkout();
    ModalClient.hide();
  };

  // Get dynamic lifetime software features based on store region
  const lifetimeSoftwareFeatures = getLifetimeSoftwareFeatures(
    cartContext.store,
  );

  const buildColorHref = (c: RareColors) => {
    const p = new URLSearchParams(searchParams?.toString() ?? '');
    p.set('color', c);
    return `?${p.toString()}`;
  };
  // No URL write on select — see ring-air ProductSelectionSection selectColor.
  const selectColor = (c: RareColors) => {
    setColor(c);
  };

  const ColorSelectionSection = (
    <Section show={true}>
      <h3 className="section-title">
        Color.{' '}
        <span className="section-title-light">
          Choose finish that suits your style.
        </span>
      </h3>
      <div className="colors">
        {RARE_COLOR_ORDER.map((rareColor, index) => {
          const colorData = RareColorNameImages[rareColor];
          const imageSrc =
            typeof colorData.image === 'string' ? colorData.image : '';
          const colorName =
            typeof colorData.name === 'string'
              ? colorData.name
              : rareColor.replace('_', ' ');

          return (
            <a
              href={buildColorHref(rareColor)}
              key={index}
              style={{ textDecoration: 'none' }}
              className={color === rareColor ? 'active' : 'non-active'}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                selectColor(rareColor);
              }}
            >
              {color === rareColor ? (
                <div className="color-content">
                  <CustomImage
                    width={110}
                    height={110}
                    src={imageSrc}
                    alt={colorName}
                  />
                  <span>{colorName}</span>
                  {colorData.tag && <div className="tag">{colorData.tag}</div>}
                  <p className="metal-info">
                    {colorName === 'Desert Snow' ? 'PT950' : '18K Gold'}
                  </p>
                </div>
              ) : (
                <div className="color-content">
                  <CustomImage
                    width={110}
                    height={110}
                    src={imageSrc}
                    alt={colorName}
                  />
                  <span>{colorName}</span>
                  {colorData.tag && <div className="tag">{colorData.tag}</div>}
                  <p className="metal-info">
                    {colorName === 'Desert Snow' ? 'PT950' : '18K Gold'}
                  </p>
                </div>
              )}
            </a>
          );
        })}
      </div>
      <div className="explore-links">
        <CustomLink href="/diesel-ultrahuman-ring/buy">
          <button
            className="gtm explore-pill"
            data-buttontype="ring style selector"
            data-product="Ultrahuman Ring Rare"
          >
            Explore Diesel Ultrahuman Ring <ArrowTopRight fill="#000" />
          </button>
        </CustomLink>
        <CustomLink href="/ring/buy">
          <button
            className="gtm explore-pill"
            data-buttontype="ring style selector"
            data-product="Ultrahuman Ring Rare"
          >
            Explore Ring AIR <ArrowTopRight fill="#000" />
          </button>
        </CustomLink>
      </div>
    </Section>
  );

  const SizeSelectionSection = (
    <Section className="size-section" show={!!color}>
      {/* Main Section Header */}
      <div className="size-section-header-main">
        <h3 className="size-section-title-main">
          Size.{' '}
          <span className="size-section-subtitle">
            Use the sizing kit to get it right the first time.
          </span>
        </h3>
        <button
          onClick={openSizeInfoModal}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
            display: 'flex',
            alignItems: 'flex-start',
          }}
          aria-label="Open ring sizing instructions"
        >
          <InfoIcon
            style={{
              width: '1.6rem',
              height: '1.6rem',
              // marginTop: '-1px',
            }}
            fill="#000"
          />
        </button>
        {/* <InfoIcon
                      style={{
                        width: '1.6rem',
                        height: 'auto',
                        marginTop: '1px',
                      }}
                      fill="#000"
                    /> */}
      </div>

      {/* Sizing Kit Card */}
      <div
        className={`sizing-kit-card ${
          !isSizingKitSelected ? 'non-selected' : ''
        } ${!isSizingKitSelected ? 'clickable' : ''}`}
        onClick={
          !isSizingKitSelected
            ? () => {
                handleSizingKitClick();
                setShowSizeSelection(false);
              }
            : undefined
        }
        onKeyDown={
          !isSizingKitSelected
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleSizingKitClick();
                  setShowSizeSelection(false);
                }
              }
            : undefined
        }
        tabIndex={!isSizingKitSelected ? 0 : undefined}
        role={!isSizingKitSelected ? 'button' : undefined}
        aria-label={
          !isSizingKitSelected ? 'Click to select sizing kit' : undefined
        }
        style={{
          cursor: !isSizingKitSelected ? 'pointer' : 'default',
        }}
      >
        <div className="sizing-kit-content">
          <div className="sizing-kit-image">
            <CustomImage
              width={74}
              height={36}
              src="web_v2/rare-buy/sizing-kit-rare.png"
              alt="Sizing Kit"
            />
          </div>
          <div className="sizing-kit-info">
            <div className="sizing-kit-header">
              <div className="sizing-kit-title">Sizing Kit</div>
              <div className="sizing-kit-free">FREE</div>
            </div>
            <div className="sizing-kit-bullets">
              <div className="bullet-point">
                <div className="check-icon">
                  <CheckIcon fill="#35633D" />
                </div>
                <div className="bullet-text">Perfect fit - no guess work</div>
              </div>
              <div className="bullet-point">
                <div className="check-icon">
                  <CheckIcon fill="#35633D" />
                </div>
                <div className="bullet-text">Eco-friendly packaging</div>
              </div>
              <div className="bullet-point">
                <div className="check-icon">
                  <CheckIcon fill="#35633D" />
                </div>
                <div className="bullet-text">Easy to share</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="shipping-info">
        <ShippingBox />
        <div className="shipping-text">Dispatches within 24 hours</div>
      </div>

      {/* Size Toggle */}
      {/* <div className="toggle-container">
                  <button
                    className="size-toggle"
                    onClick={() => {
                      console.log(
                        'Toggle clicked, current state:',
                        showSizeSelection,
                      );
                      setShowSizeSelection(!showSizeSelection);
                    }}
                    aria-expanded={showSizeSelection}
                    aria-label="Toggle sizing kit information"
                  >
                    <div className="toggle-text">I have a ring sizing kit</div>
                    <div className="chevron-icon">
                      <Image
                        src={ChevronRight}
                        alt="chevron_right"
                        width={7}
                        height={12}
                        style={{
                          transform: showSizeSelection
                            ? 'rotate(-90deg)'
                            : 'rotate(90deg)',
                          transition: 'transform 0.3s ease-in-out',
                        }}
                      />
                    </div>
                  </button>
                  <button
                    onClick={openSizeInfoModal}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 0,
                      display: 'flex',
                      alignItems: 'flex-start',
                    }}
                    aria-label="Open ring sizing instructions"
                  >
                    <InfoIcon
                      style={{
                        width: '1.6rem',
                        height: '1.6rem',
                        // marginTop: '-1px',
                      }}
                      fill="#000"
                    />
                  </button>
                </div> */}
      {/* Size Selection Section */}
      <div
        className={`size-selection-section ${
          showSizeSelection ? 'active' : ''
        }`}
      >
        <div className="size-section-header">
          {/* <h4 className="size-section-title">
                          I know my Ring size
                        </h4> */}
        </div>
        <div className="shipping-info">
          <ShippingBox />
          <div className="shipping-text">
            {(() => {
              // Calculate tomorrow's date in "27 Sep" format
              // const months = [
              //   'Jan',
              //   'Feb',
              //   'Mar',
              //   'Apr',
              //   'May',
              //   'Jun',
              //   'Jul',
              //   'Aug',
              //   'Sep',
              //   'Oct',
              //   'Nov',
              //   'Dec',
              // ];
              const today = new Date();
              const tomorrow = new Date(today);
              tomorrow.setDate(today.getDate() + 1);
              // const day = tomorrow.getDate();
              // const month = months[tomorrow.getMonth()];
              // const formattedDate = `${day} ${month}`;
              return <>{shippingEstimate}</>;
            })()}
          </div>
        </div>
        <div className="sizes">
          {Object.values(RingSizes)
            .filter(
              (value) => typeof value === 'number' && value !== RingSizes.OPEN,
            )
            .map((ringSize, index) => (
              <button
                key={index}
                className={size === ringSize ? 'active' : ''}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleSizeSelection(ringSize as RingSizes);
                }}
              >
                <div className="size-content">{ringSize}</div>
              </button>
            ))}
        </div>
      </div>
    </Section>
  );

  const PowerplugsSelectionSection = (
    <Section show={!!color && !!size}>
      <div className="size-section-header-main">
        <h3 className="size-section-title-main">
          Lifetime software plug-ins access at no cost.{' '}
          <span className="size-section-subtitle">Current and future.</span>
        </h3>
        <button
          onClick={openPowerPlugsInfoModal}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <InfoIcon
            style={{
              width: '1.6rem',
              height: '1.6rem',
              marginTop: '1px',
            }}
            fill="#000"
          />
        </button>
      </div>

      {/* Lifetime Software Access Card */}
      <div className="lifetime-software-card">
        <div className="lifetime-software-content">
          <div className="lifetime-software-header">
            <div className="lifetime-software-exclusive-tag">
              Rare Privileges
            </div>
            <div className="lifetime-software-title-section">
              <div className="lifetime-software-title">
                All PowerPlugs included
              </div>
              <div className="lifetime-software-pricing">
                {/* <span className="lifetime-software-price-crossed">
                              $36
                            </span> */}
                <span className="lifetime-software-price-value">
                  (Valued at {currencySymbol}
                  {getLifetimeSoftwarePrice.toLocaleString()}/year)
                </span>
              </div>
            </div>
          </div>

          <div className="lifetime-software-divider"></div>

          <div className="lifetime-software-features">
            {lifetimeSoftwareFeatures.map((feature, index) => (
              <div
                key={feature.id || index}
                className="lifetime-software-feature"
              >
                <div className="lifetime-software-check-icon">
                  <CheckIcon fill="#35633D" />
                </div>
                <div className="lifetime-software-feature-text">
                  {feature.name}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Learn More Button */}
      <div className="lifetime-software-learn-more">
        <LearnMoreButton
          onClick={() => {
            openPowerPlugsInfoModal();
          }}
          style={{
            backgroundColor: '#F5F1ED',
          }}
        />
      </div>
    </Section>
  );

  const UHXSelectionSection = (
    <Section show={!!color && !!size}>
      <div className="size-section-header-main">
        <h3 className="size-section-title-main">
          Extra coverage for life.{' '}
          <span className="size-section-subtitle">Protect your precious.</span>
        </h3>
        <button
          onClick={openUhxInfoModal}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <InfoIcon
            style={{
              width: '1.6rem',
              height: '1.6rem',
              marginTop: '1px',
            }}
            fill="#000"
          />
        </button>
      </div>

      {/* UHX Coverage Card */}
      <div className="uhx-coverage-card">
        <div className="uhx-coverage-content">
          <div className="uhx-coverage-header">
            <div className="uhx-coverage-lifetime-tag">Rare Privileges</div>
            <div className="uhx-coverage-title-section">
              <div className="uhx-coverage-title">UltrahumanX Membership</div>
              <div className="uhx-coverage-pricing">
                <span className="uhx-coverage-price-value">
                  (Valued at {currencySymbol}
                  {getUhxCoveragePrice.toLocaleString()}/year)
                </span>
              </div>
            </div>
          </div>

          <div className="uhx-coverage-divider"></div>

          <div className="uhx-coverage-features">
            <div className="uhx-coverage-feature">
              <div className="uhx-coverage-check-icon">
                <CheckIcon fill="#35633D" />
              </div>
              <div className="uhx-coverage-feature-text">
                Lifetime UltrahumanX membership
              </div>
            </div>
            <div className="uhx-coverage-feature">
              <div className="uhx-coverage-check-icon">
                <CheckIcon fill="#35633D" />
              </div>
              <div className="uhx-coverage-feature-text">Priority care</div>
            </div>
            <div className="uhx-coverage-feature">
              <div className="uhx-coverage-check-icon">
                <CheckIcon fill="#35633D" />
              </div>
              <div className="uhx-coverage-feature-text">
                One-time size protection
              </div>
            </div>
            <div className="uhx-coverage-feature">
              <div className="uhx-coverage-check-icon">
                <CheckIcon fill="#35633D" />
              </div>
              <div className="uhx-coverage-feature-text">
                Exclusive upgrade program
              </div>
            </div>
            <div className="uhx-coverage-feature">
              <div className="uhx-coverage-check-icon">
                <CheckIcon fill="#35633D" />
              </div>
              <div className="uhx-coverage-feature-text">
                Replacement coverage
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Learn More Button */}
      <div className="uhx-coverage-learn-more">
        <LearnMoreButton
          onClick={() => {
            openUhxInfoModal();
          }}
          style={{
            backgroundColor: '#F5F1ED',
          }}
        />
      </div>
    </Section>
  );

  const OverviewSection = (
    <Section show={!!color && !!size}>
      <div className="summary-card">
        <div className="summary-content">
          <div className="summary-header">
            <div className="summary-title">
              Almost there. Here’s your order summary.
            </div>

            <div className="summary-product-details">
              <div className="summary-product-info">
                <div className="summary-product-name">Ultrahuman Ring Rare</div>
                <div className="summary-product-variant">
                  {color
                    ? typeof RareColorNameImages[color]?.name === 'string'
                      ? RareColorNameImages[color].name
                      : color.replace('_', ' ')
                    : ''}
                  {size === RingSizes.OPEN
                    ? ', Sizing Kit included'
                    : size && typeof size === 'number'
                    ? `, Size ${size}`
                    : ''}
                </div>
              </div>
            </div>

            <div className="summary-pricing-breakdown">
              <div className="summary-price-item">
                <div className="summary-price-label">Ring Rare</div>
                <div className="summary-price-value">
                  {currencySymbol}
                  {formatPriceWithComma(
                    affiliateDiscount > 0 ? price : effectivePrice,
                  )}
                </div>
              </div>

              {/* Show affiliate discount if applicable */}
              {affiliateDiscount > 0 &&
                effectiveDiscount.type === 'affiliate' && (
                  <div className="price-breakup">
                    <p>
                      {t('ring.productSelection.cartSummary.priceBreakup.b')}
                    </p>
                    <p>
                      -{currencySymbol}
                      {formatPriceWithComma(affiliateDiscount)}
                    </p>
                  </div>
                )}

              <div className="summary-divider"></div>

              <div className="summary-price-item">
                <div className="summary-price-label">UltrahumanX Coverage</div>
                <div className="summary-price-value">FREE</div>
              </div>

              <div className="summary-divider"></div>

              <div className="summary-price-item">
                <div className="summary-price-label">
                  Software plug-ins access
                </div>
                <div className="summary-price-value">FREE</div>
              </div>

              <div className="summary-divider"></div>

              <div className="summary-total">
                <div className="summary-total-label">Total</div>
                <div className="summary-total-value">
                  {currencySymbol}
                  {formatPriceWithComma(effectivePrice)}
                </div>
              </div>

              <div className="summary-taxes-note">
                {shouldShowTaxNote() && '* Taxes are calculated at checkout'}
              </div>

              <div className="summary-divider"></div>
            </div>

            <div className="summary-dispatch-info">{shippingEstimate}</div>
          </div>

          <div className="summary-checkout-section">
            <div className="summary-checkout-button-wrapper">
              <button
                className="summary-checkout-button"
                onClick={handleCheckout}
                aria-busy={isLoading}
                disabled={notServicable || !color || !size || isLoading}
              >
                <span>
                  {notServicable
                    ? 'Not Available'
                    : isLoading
                    ? 'Adding...'
                    : 'Add to cart'}
                </span>
              </button>
            </div>

            {cartContext.store === ShopifyStore.US && (
              <div className="summary-hsa-fsa-info">
                <div className="summary-hsa-fsa-icon">
                  <HsaFsaCheckIcon fill="#000" />
                </div>
                <div className="summary-hsa-fsa-text">
                  HSA/FSA Eligible | Save more with your pre-tax dollars
                </div>
              </div>
            )}
          </div>

          <div className="summary-features-footer">
            <div className="summary-features-grid">
              <div className="summary-feature-item">
                <div className="summary-feature-icon">
                  <Charger fill="#000" />
                </div>
                <div className="summary-feature-text">Charger included</div>
              </div>

              <div className="summary-feature-item">
                <div className="summary-feature-icon">
                  <Battery fill="#000" />
                </div>
                <div className="summary-feature-text">
                  Up to 6 days battery life
                </div>
              </div>

              <div className="summary-feature-item">
                <div className="summary-feature-icon">
                  <Return fill="#000" />
                </div>
                <div className="summary-feature-text">30-day return</div>
              </div>
            </div>

            <div className="summary-disclaimer">
              *Please note that additional local taxes or carrier fees may apply
              upon delivery
            </div>
          </div>
        </div>
      </div>
    </Section>
  );

  return (
    <RareProductSelectionSectionWrapper bgColor={bgColor}>
      <OffersModalNewProvider>
        {/* <TitleBar title={title} /> */}

        {offersData && (
          <PrimaryOffersSlot>
            <DesktopPrimaryOffers>
              <PrimaryOffers
                data={offersData}
                $backgroundColor={offersBackgroundColor}
                $dealsBackgroundColor={dealsBackgroundColor}
              />
            </DesktopPrimaryOffers>
            <MobilePrimaryOffers>
              <PrimaryOffersTransitionTile
                data={offersData}
                backgroundColor={dealsBackgroundColor}
                dealsBackgroundColor={dealsBackgroundColor}
              />
            </MobilePrimaryOffers>
          </PrimaryOffersSlot>
        )}

        <PanelContainer>
          {/* Left Panel - Desktop Carousel */}
          <LeftPanel>
            <StickyPanel $headerVisible={globalUI.headerVisible ?? true}>
              <RareCarousel
                items={
                  RareCarouselData[color as RareColors] ??
                  RareCarouselData[RareColors.DESERT_ROSE] ??
                  []
                }
              />
            </StickyPanel>
          </LeftPanel>

          {/* Right Panel - Buy Section */}
          <RightPanel>
            <div>
              {/* Exact structure matching the image flow */}
              {/* Section 1: Title, Pricing, Subtitle, Offers Carousel */}
              <Section show={true}>
                <div className="main-title">Ring Rare</div>
                <p className="main-subtitle">
                  The world’s first luxury smart ring. Crafted in 18K Gold and
                  Platinum.
                </p>

                {/* Mobile Currency Dropdown */}
                <div className="currencyDropdown">
                  <RegionSelector />
                </div>

                {/* Show not servicable message or pricing */}
                {notServicable ? (
                  NotServicableMessage
                ) : (
                  <div className="pricing-display">
                    {/* Show affiliate discount strikethrough */}
                    {affiliateDiscount > 0 &&
                      effectiveDiscount.type === 'affiliate' && (
                        <span
                          style={{
                            textDecoration: 'line-through',
                            color: '#999',
                            marginRight: '12px',
                            // fontSize: '1.5rem',
                          }}
                        >
                          {currencySymbol}
                          {formatPriceWithComma(price)}
                        </span>
                      )}
                    {currencySymbol}
                    {formatPriceWithComma(effectivePrice)}
                    {affiliateDiscount > 0 &&
                      effectiveDiscount.type === 'affiliate' && (
                        <div
                          className="offerFlex"
                          style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '8px',
                            padding: '8px 0',
                            // marginTop: '8px',
                          }}
                        >
                          <p className="offerTag">
                            <span>
                              <span className="offerApplied">
                                {affiliateDetails?.code
                                  ? affiliateDetails.code
                                  : ''}
                              </span>{' '}
                              Offer Applied
                            </span>
                          </p>
                        </div>
                      )}
                    {/* Show regional discount strikethrough if no affiliate discount */}
                    {!(
                      affiliateDiscount > 0 &&
                      effectiveDiscount.type === 'affiliate'
                    ) &&
                      (process.env.NEXT_PUBLIC_SHOW_RARE_STRIKED_PRICES ===
                        'true' ||
                        searchParams?.get('showStrikedPrices') === 'true' ||
                        showStrikedPrice) &&
                      isDiscountEnabled &&
                      priceDrop > 0 && (
                        <>
                          <span
                            style={{
                              textDecoration: 'line-through',
                              color: '#999',
                              marginLeft: '12px',
                              fontSize: '1.5rem',
                            }}
                          >
                            {currencySymbol}{' '}
                            {formatPriceWithComma(price + priceDrop)}
                          </span>
                          <div
                            style={{
                              color: '#e74c3c',
                              fontSize: '1rem',
                              marginTop: '8px',
                            }}
                          >
                            Save {currencySymbol}
                            {''}
                            {formatPriceWithComma(priceDrop)}
                          </div>
                        </>
                      )}
                  </div>
                )}

                {/* <div
                  className="section-subtitle"
                  style={{
                    marginBottom: '24px',
                    fontSize: '1.1rem',
                    color: '#666',
                  }}
                >
                  The world&apos;s most advanced smart ring with rare earth
                  materials and exclusive finishes
                </div> */}

                {/* Offers Carousel would go here */}
                {/* <div
                  style={{
                    marginBottom: '32px',
                    padding: '16px',
                    backgroundColor: '#f8f8f8',
                    borderRadius: '8px',
                  }}
                >
                  <div
                    style={{
                      fontSize: '0.9rem',
                      color: '#666',
                      marginBottom: '8px',
                    }}
                  >
                    Special Offers
                  </div>
                  <div style={{ fontSize: '1rem', fontWeight: 'bold' }}>
                    🎁 Limited time: Free premium charging dock
                  </div>
                </div> */}
                {!notServicable && (
                  <>
                    {secondaryOfferData && (
                      <OffersCarousel
                        data={secondaryOfferData}
                        elementBgColor={dealsBackgroundColor}
                        leftAligned={true}
                        $isRarePage={true}
                      />
                    )}
                    <div className="eta-container">
                      <div className="eta-icon">
                        <Image
                          src={DeliveryIcon}
                          alt="chevron_right"
                          width={20}
                          height={20}
                        />
                      </div>
                      <div className="eta-text">
                        <span className="eta-text-normal">
                          {shippingEstimate}
                        </span>
                      </div>
                    </div>
                  </>
                )}
              </Section>

              {/* Mobile Carousel Section - Only visible on mobile */}
              <div className="carousel-section">
                <RareCarousel
                  items={
                    RareCarouselData[color as RareColors] ??
                    RareCarouselData[RareColors.DESERT_ROSE] ??
                    []
                  }
                />
              </div>

              {/* Section 2: Color Selection */}
              {ColorSelectionSection}

              {/* Section 4: Complete Size Selection Section */}
              {SizeSelectionSection}

              {/* Section 5: Lifetime Software Plugin Access */}
              {PowerplugsSelectionSection}

              {/* Section 6: UHX Section */}
              {UHXSelectionSection}

              {/* Section 7: Summary and Checkout */}
              {OverviewSection}
            </div>
          </RightPanel>
        </PanelContainer>
      </OffersModalNewProvider>
    </RareProductSelectionSectionWrapper>
  );
};

export default RareProductSelectionSection;
